import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getVenueByHost } from "./api";
import { usePreviewOverlay } from "./preview-overlay";
import { accentForegroundFor } from "./contrast";
import { googleWeightsFor } from "./font";
import type { Venue, VenueResponse } from "./types";

const KNOWN_FONTS = new Set([
  "JetBrains Mono",
  "Inter",
  "Space Grotesk",
  "Syne",
  "DM Sans",
  "Plus Jakarta Sans",
  "Source Sans 3",
]);

// Luminance check, matches src/lib/color-utils.ts in the Next.js app.
function isLightColor(hex: string): boolean {
  const m = hex.replace(/^#/, "");
  if (m.length !== 6) return false;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
}

function applyVenueTheme(venue: Venue) {
  const root = document.documentElement.style;
  if (venue.accentColor) root.setProperty("--accent", venue.accentColor);
  if (venue.backgroundColor) {
    root.setProperty("--bg", venue.backgroundColor);
    // Body text contrasts the BACKGROUND (dark bg → light text, light bg →
    // dark text), mirroring the Next.js venue layout. Must NOT be driven by
    // textColor, that's the on-accent colour and would black out body text
    // on a dark background.
    root.setProperty(
      "--fg",
      isLightColor(venue.backgroundColor) ? "#0a0a0a" : "#ededed",
    );
  }
  // textColor is the on-accent text colour (buttons/pills), but venues often
  // set it as their BODY text colour (e.g. dark text + dark accent), which
  // would paint black-on-black buttons. CONTRAST GUARD: only honour textColor
  // when it sits on the opposite luminance side of the accent; otherwise
  // derive a legible colour from the accent itself.
  root.setProperty("--accent-fg", accentForegroundFor(venue.accentColor, venue.textColor));
  if (venue.fontFamily) {
    root.setProperty(
      "--venue-font",
      `'${venue.fontFamily}', ui-monospace, monospace`,
    );
    if (KNOWN_FONTS.has(venue.fontFamily)) {
      ensureGoogleFont(venue.fontFamily);
    }
  }
  // Per-venue browser favicon (+ apple-touch-icon): point the tab icon at the
  // venue's own icon so e.g. the Madruga tab shows Madruga, not the Assembly
  // triangle. Falls back to the default when the venue has no icon.
  if (venue.appIconUrl) {
    const icons = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]');
    if (icons.length) {
      icons.forEach((l) => {
        l.href = venue.appIconUrl!;
        l.removeAttribute("type"); // let the browser sniff (png/svg)
      });
    } else {
      const l = document.createElement("link");
      l.rel = "icon";
      l.href = venue.appIconUrl;
      document.head.appendChild(l);
    }
    let touch = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (!touch) {
      touch = document.createElement("link");
      touch.rel = "apple-touch-icon";
      document.head.appendChild(touch);
    }
    touch.href = venue.appIconUrl;
  }
}

function ensureGoogleFont(family: string) {
  const id = `gf-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const slug = family.replace(/\s+/g, "+");
  const wght = googleWeightsFor(family).join(";");
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${slug}:wght@${wght}&display=swap`;
  document.head.appendChild(link);
}

// Hook that loads the current venue (by hostname) and applies its theme to the
// document. All venue pages call this so the theme survives client-side route
// changes.
// Resolve the venue host. In dev only, a `?host=` query param overrides the
// real host so you can preview any venue locally (against the staging API via
// the Vite proxy) with hot-reload, e.g.
//   localhost:5173/festival/<id>?host=madruga.aws-staging.assemblytickets.com
// The override is gated on import.meta.env.DEV so it never applies in prod.
function resolvePreviewHost(): string {
  if (typeof window === "undefined") return "";
  if (import.meta.env.DEV) {
    const override = new URLSearchParams(window.location.search).get("host");
    if (override) return override;
  }
  return window.location.host;
}

export function useVenue() {
  const host = resolvePreviewHost();
  const cacheKey = `assembly:venue:${host}`;
  // Live-draft preview: when this site runs inside the admin's preview iframe,
  // unsaved editor changes arrive as an appConfig overlay (preview-overlay.ts)
  // and are merged over the fetched config below, so the preview reflects
  // keystrokes instantly without writing anything.
  const overlay = usePreviewOverlay();
  const query = useQuery({
    queryKey: ["venue", host],
    queryFn: () => getVenueByHost(host),
    enabled: !!host,
    // Seed from the last-known venue for this host so the themed app paints
    // instantly on relaunch (no loading flash before the green), then refetch
    // in the background. The white-label native apps always load the same
    // venue, so this is a cache hit on every launch after the first.
    initialData: (): VenueResponse | undefined => {
      try {
        const c = typeof window !== "undefined" ? localStorage.getItem(cacheKey) : null;
        return c ? (JSON.parse(c) as VenueResponse) : undefined;
      } catch {
        return undefined;
      }
    },
    initialDataUpdatedAt: 0, // treat the cached copy as stale → background refresh
  });

  useEffect(() => {
    if (query.data?.venue) {
      applyVenueTheme(query.data.venue);
      try {
        localStorage.setItem(cacheKey, JSON.stringify(query.data));
      } catch {
        /* storage unavailable, non-fatal */
      }
    }
  }, [query.data?.venue, cacheKey]);

  // Apply the preview overlay (draft-over-saved, key-level merge). Memoised so
  // consumers don't re-render on unrelated changes. No-op outside the preview.
  const data = useMemo(() => {
    if (!query.data?.venue || !overlay) return query.data;
    return {
      ...query.data,
      venue: {
        ...query.data.venue,
        appConfig: { ...(query.data.venue.appConfig ?? {}), ...overlay },
      },
    };
  }, [query.data, overlay]);

  return { ...query, data, host };
}

export function initialFor(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "·";
}
